import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import {
  CreateZonePostalCodeCommand,
  CustomResultOfZoneDto,
  GetZonePostalCodeByPostalCodeDto,
  UpdateZonePostalCodeCommand,
  ZoneDto,
} from 'src/app/core/api/models';
import {
  ZonePostalCodesService,
  ZonesService,
} from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { RegularExpressions } from 'src/app/core/constants/regular-expressions';
import { DialogService } from 'src/app/core/services/dialog.service';
import { BaseFormPageComponent } from '../../../shared/base/components/base-form-page.component';
import { FormPageConfig } from './../../../../core/models/form-page-config.model';
import { BaseFormModule } from 'src/app/pages/shared/base/modules/base-form.module';

@Component({
    selector: 'app-zone-postal-codes-form',
    templateUrl: './zone-postal-codes-form.component.html',
    imports: [BaseFormModule]
})
export class ZonePostalCodesFormComponent
  extends BaseFormPageComponent
  implements OnInit
{
  //#region Properties

  formPageConfig: FormPageConfig<any> = {};
  model: GetZonePostalCodeByPostalCodeDto = {};
  protected currentService = inject(ZonePostalCodesService);

  //zones
  selectedState: number;
  cityId: number = -1;
  postalCode: any;

  //#endregion

  constructor(
    private zoneService: ZonesService,
    public dialogService: DialogService,
    public translate: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.changeValidation();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  //#region init methods

  initForm() {
    this.form = new FormGroup({
      province: new FormControl(''),
      city: new FormControl(''),
      zoneId: new FormControl(''),
      postalCode: new FormControl(''),
      title: new FormControl('', Validators.required),
    });
  }

  changeValidation() {
    if (!this.formPageConfig.isEditMode) {
      this.form
        .get('postalCode')
        .setValidators([
          Validators.required,
          Validators.minLength(4),
          Validators.pattern(RegularExpressions.justNumber),
        ]);

      this.form.get('province').setValidators(Validators.required);
      this.form.get('city').setValidators(Validators.required);
      this.form.get('zoneId').setValidators(Validators.required);
    } else {
      this.form.get('postalCode').clearValidators();
      this.form.get('postalCode').disable();
      this.form.get('province').clearValidators();
      this.form.get('province').disable();
      this.form.get('city').clearValidators();
      this.form.get('city').disable();
      this.form.get('zoneId').clearValidators();
      this.form.get('zoneId').disable();
    }

    this.form.get('postalCode').updateValueAndValidity();
    this.form.get('zoneId').updateValueAndValidity();
    this.form.get('city').updateValueAndValidity();
    this.form.get('province').updateValueAndValidity();
  }

  initModel() {
    try {
      // when form is edit mode
      if (this.formPageConfig.isEditMode) {
        this.model = Object.assign({}, this.formPageConfig.editData);
        this.postalCode = this.formPageConfig.editId.postalCode;
      }
      if (this.model.zoneId) this.getSelectedCity(this.model.zoneId);
      // when form is create mode
      else {
        this.postalCode = null;
        this.selectedState = null;
        this.cityId = null;
        this.formPageConfig.editData = {};
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }
  //#endregion

  //#region Api

  //Create
  baseCreateApi(model: CreateZonePostalCodeCommand) {
    this.unSubs();

    this.loading = true;
    model.postalCode = this.postalCode;

    this.currentService
      .zonePostalCodesCreatePost({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //Update
  baseUpdateApi(model: UpdateZonePostalCodeCommand) {
    this.unSubs();
    this.loading = true;

    this.currentService
      .zonePostalCodesUpdatePut({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  // get zone by id
  getZoneByIdApi(zoneId: number): Observable<CustomResultOfZoneDto> {
    return this.zoneService.zonesGetZoneById({ Id: zoneId });
  }

  //#endregion

  checkCreateOrUpdate() {
    if (this.loading || !this.form.valid) return;
    this.model.title = this.model.title.trim();

    if (this.formPageConfig.isEditMode)
      this.baseUpdateApi({
        postalCode: this.formPageConfig.editId.postalCode,
        title: this.model.title,
        zoneId: this.formPageConfig.editData.zoneId,
      });
    else this.baseCreateApi(this.model);
  }

  //#region zone configs
  getSelectedCity(zoneId) {
    this.unSubs();

    //get city by zoneId
    this.getZoneByIdApi(zoneId)
      .pipe(
        map((res) => res.data),
        map((zone: ZoneDto) => {
          this.getSelectedState(zone?.parentId);
          this.cityId = zone?.parentId;
          this.model.zoneId = zone.id;
          return zone;
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe();
  }

  getSelectedState(stateId: number) {
    this.unSubs();

    //get city by zoneId
    this.getZoneByIdApi(stateId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((city) => {
        this.selectedState = city.data.parentId;
      });
  }

  onChangeZones(zoneId) {
    this.cityId = -1;
    this.selectedState = zoneId;
  }

  onCityChange($event) {
    this.cityId = $event;
  }

  //#endregion

  trimTitle() {
    this.model.title = this.model.title.trim();
  }
}
